import { useEffect, useState, useContext, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import { DarkModeContext } from "../context/DarkModeContext";
import { BACKEND_URL } from "../utils";
import axios from "axios";

import { motion } from "framer-motion";
import gsap from "gsap";

const Dashboard = () => {
  const role = localStorage.getItem("role");
  const { darkMode } = useContext(DarkModeContext);
  const [data, setData] = useState(null);

  const titleRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/dashboard`)
      .then((res) => setData(res.data))
      .catch((err) => console.error(err));
  }, []);

  // 🔥 GSAP TEXT ANIMATION
  useEffect(() => {
    const text = titleRef.current;
    const letters = text.innerText.split("");

    text.innerHTML = letters
      .map((letter) => `<span class="letter inline-block">${letter}</span>`)
      .join("");

    gsap.fromTo(
      ".letter",
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        stagger: 0.04,
        duration: 0.5,
        ease: "power3.out",
      }
    );
  }, []);

  if (role !== "Manager") return <Navigate to="/products" />;

  const cardBg = darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900";
  const cardTitle = darkMode ? "text-gray-300" : "text-gray-600";

  return (
    <div
      ref={containerRef}
      className={`${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      } p-4 md:p-8 min-h-screen overflow-hidden`}
    >
      {/* 🔥 TITLE */}
      <h1
        ref={titleRef}
        className="text-2xl md:text-3xl font-bold mb-4 text-center md:text-left overflow-hidden"
      >
        Dashboard
      </h1>

      {data ? (
        <>
          {/* 🔥 CARDS */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.15,
                },
              },
            }}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6"
          >
            {[
              { title: "Total Earning", value: `$${data.totalEarning}` },
              { title: "Total Views", value: data.totalViews },
              { title: "Total Sales", value: data.totalSales },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0px 15px 30px rgba(0,0,0,0.2)",
                }}
                transition={{ type: "spring", stiffness: 200 }}
                className={`${cardBg} p-4 shadow rounded-xl backdrop-blur-md`}
              >
                <h2 className={`${cardTitle}`}>{item.title}</h2>
                <p className="text-2xl font-semibold">{item.value}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* 🔥 CHARTS */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.2,
                  delayChildren: 0.2,
                },
              },
            }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
          >
            {/* BAR */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.02 }}
              className={`${cardBg} p-4 shadow rounded-xl`}
            >
              <h2 className={`${cardTitle} mb-3 text-center md:text-left`}>
                Monthly Earnings
              </h2>

              {data.chartData ? (
                <Bar
                  data={data.chartData}
                  options={{
                    responsive: true,
                    animation: {
                      duration: 1200,
                      easing: "easeOutQuart",
                    },
                    plugins: {
                      legend: {
                        labels: { color: darkMode ? "#fff" : "#000" },
                      },
                      title: {
                        display: true,
                        text: "Monthly Earnings",
                        color: darkMode ? "#fff" : "#000",
                      },
                    },
                    scales: {
                      x: { ticks: { color: darkMode ? "#fff" : "#000" } },
                      y: { ticks: { color: darkMode ? "#fff" : "#000" } },
                    },
                  }}
                />
              ) : (
                <p className="text-center">Loading chart...</p>
              )}
            </motion.div>

            {/* LINE */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.02 }}
              className={`${cardBg} p-4 shadow rounded-xl`}
            >
              <h2 className={`${cardTitle} mb-3 text-center md:text-left`}>
                Sales Overview
              </h2>

              {data.chartData ? (
                <Line
                  data={data.chartData}
                  options={{
                    responsive: true,
                    animation: {
                      duration: 1200,
                      easing: "easeOutQuart",
                    },
                    plugins: {
                      legend: {
                        labels: { color: darkMode ? "#fff" : "#000" },
                      },
                      title: {
                        display: true,
                        text: "Sales Overview",
                        color: darkMode ? "#fff" : "#000",
                      },
                    },
                    scales: {
                      x: { ticks: { color: darkMode ? "#fff" : "#000" } },
                      y: { ticks: { color: darkMode ? "#fff" : "#000" } },
                    },
                  }}
                />
              ) : (
                <p className="text-center">Loading chart...</p>
              )}
            </motion.div>
          </motion.div>
        </>
      ) : (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          Loading dashboard data...
        </motion.p>
      )}
    </div>
  );
};

export default Dashboard;